// Ambient sound, the client's half: a player for the server's W X Y stream (simulation/src/audio_stream.py has the wire).
//
// The server makes the sound (its ear synthesizes it from the world); this file only plays it. The stream is horizontal first-order ambisonics in the
// world's frame (X east, Y north), so it does not depend on which way the viewer looks. The player rotates it by the camera heading and decodes it to four
// virtual loudspeakers at +-45 and +-135 degrees, each of which goes through the browser's own HRTF panner (PannerNode), so the headphones get the direction,
// including front and back. Turning the head costs nothing and never waits for the server.
//
// Audio runs in an AudioWorklet (below, as a source string so any bundler can ship it): a jitter buffer, the rotation and the decode. Nothing is sent upstream
// except the opt-in message {"type":"audio","on":true}; the heading never leaves the browser. A server that cannot make the sound answers {"type":"audio_unavailable","why":...}
// (unavailable()), and the player switches itself off.
//
// Conventions (sim_core/src/audio/wxy_synth.hpp): a bearing is the direction a sound comes FROM, counter-clockwise from east. The heading given to setHeading() is
// the direction the viewer FACES in the same terms (east 0, north pi/2). FuMa-style W: a plane wave of pressure s from bearing a is W = s / sqrt(2), X = s cos a, Y = s sin a.
//
// A browser may only start audio from a user gesture: enable() must be called from a click or a key press.

export const AUDIO_RATE = 32000;          // samples per second per channel (the server's SAMPLE_RATE)
export const AUDIO_BLOCK = 1024;          // samples per channel in one frame (the server's BLOCK)
export const AUDIO_CHANNELS = 3;          // W, X, Y
export const AUDIO_HEAD = 24;             // header bytes before the samples
const PRIME_BLOCKS = 6;                   // playback starts when this many blocks are buffered (about 190 ms), and again after an underrun
const MAX_QUEUE_BLOCKS = 24;              // never more than about 770 ms behind: older blocks are dropped (a tab that was in the background)
const SPEAKERS = 4;
const WORKLET_NAME = 'ambient-wxy';

/** A binary websocket message that is an audio frame: "AMB", version 1, marker 0xFF at byte 7. */
export function isAudioFrame(buf: ArrayBuffer): boolean {
  if (buf.byteLength < AUDIO_HEAD) return false;
  const b = new Uint8Array(buf, 0, 8);
  return b[0] === 0x41 && b[1] === 0x4D && b[2] === 0x42 && b[3] === 1 && b[7] === 0xFF;
}

export interface AmbientStats {
  received: number;      // blocks that arrived
  gaps: number;          // blocks the server skipped (a stalled socket)
  underruns: number;     // times playback ran dry and re-buffered
  overruns: number;      // old blocks dropped to bound the delay
  resets: number;        // times the stream started over (a reconnect)
  buffered: number;      // samples waiting, per channel
  playing: boolean;
  gainDb: number;        // the server's adaptive gain of the newest block
}

// The worklet. Plain JavaScript in a string (it runs in the AudioWorkletGlobalScope, which has none of this file). Tested in Node with stubs (tools/audio/t_worklet.mjs).
//
// Per sample: W X Y -> rotate X, Y into the head's frame (front, left) -> four speaker feeds
//   feed_j = k (sqrt(2) W + 2 (Xh cos th_j + Yh sin th_j)),  k = 1 / sqrt(3 N),  N = 4,
// the energy-preserving first-order decode for N speakers in a ring. th_j are the speakers' angles counter-clockwise from the front.
export const WORKLET_SRC = `
class AmbientWxy extends AudioWorkletProcessor {
  constructor(options) {
    super();
    const o = options.processorOptions;
    this.B = o.block;
    this.prime = o.prime;
    this.maxQueue = o.maxQueue;
    this.queue = [];
    this.pos = 0;
    this.playing = false;
    this.ramp = 0;
    this.lastSeq = -1;
    this.gainDb = 0;
    this.pc = 1; this.ps = 0;
    this.tc = 1; this.ts = 0;
    this.received = 0; this.gaps = 0; this.underruns = 0; this.overruns = 0; this.resets = 0;
    this.sinceReport = 0;
    const th = [0.25, 0.75, -0.75, -0.25].map(function (v) { return v * Math.PI; });
    this.cs = th.map(Math.cos);
    this.sn = th.map(Math.sin);
    this.k = 1 / Math.sqrt(12);
    const self = this;
    this.port.onmessage = function (e) { self.onBlock(e.data); };
  }
  onBlock(m) {
    if (m.heading) { this.tc = m.heading[0]; this.ts = m.heading[1]; return; }
    if (m.seq <= this.lastSeq) {
      this.queue.length = 0; this.pos = 0; this.playing = false; this.resets++;
    } else if (this.lastSeq >= 0 && m.seq !== this.lastSeq + 1) {
      this.gaps++;
    }
    this.lastSeq = m.seq;
    this.gainDb = m.gainDb;
    this.queue.push(m.pcm);
    this.received++;
    while (this.queue.length > this.maxQueue) { this.queue.shift(); this.pos = 0; this.overruns++; }
  }
  report() {
    this.port.postMessage({
      received: this.received, gaps: this.gaps, underruns: this.underruns, overruns: this.overruns, resets: this.resets,
      buffered: this.queue.length * this.B - this.pos, playing: this.playing, gainDb: this.gainDb,
    });
  }
  process(inputs, outputs) {
    const out = outputs[0];
    const n = out[0].length;
    const B = this.B;
    const c0 = this.pc, s0 = this.ps;
    let c1 = this.tc, s1 = this.ts;
    const m1 = Math.hypot(c1, s1);
    if (m1 > 1e-6) { c1 /= m1; s1 /= m1; } else { c1 = c0; s1 = s0; }
    this.pc = c1; this.ps = s1;
    const root2 = Math.SQRT2, k = this.k, cs = this.cs, sn = this.sn;
    for (let i = 0; i < n; i++) {
      if (!this.playing) {
        if (this.queue.length >= this.prime) { this.playing = true; this.ramp = 0; }
        else { for (let j = 0; j < 4; j++) out[j][i] = 0; continue; }
      }
      const blk = this.queue[0];
      if (blk === undefined) {
        this.playing = false; this.underruns++;
        for (let j = 0; j < 4; j++) out[j][i] = 0;
        continue;
      }
      const idx = this.pos;
      const W = blk[idx] / 32768, X = blk[B + idx] / 32768, Y = blk[2 * B + idx] / 32768;
      if (++this.pos >= B) { this.queue.shift(); this.pos = 0; }
      if (this.ramp < 1) this.ramp = Math.min(1, this.ramp + 1 / 512);
      const f = (i + 1) / n;
      let c = c0 + (c1 - c0) * f, s = s0 + (s1 - s0) * f;
      const m = Math.hypot(c, s);
      if (m > 1e-6) { c /= m; s /= m; }
      const xh = X * c + Y * s, yh = -X * s + Y * c, r = root2 * W, g = k * this.ramp;
      for (let j = 0; j < 4; j++) out[j][i] = g * (r + 2 * (xh * cs[j] + yh * sn[j]));
    }
    this.sinceReport += n;
    if (this.sinceReport >= sampleRate / 2) { this.sinceReport = 0; this.report(); }
    return true;
  }
}
registerProcessor('${WORKLET_NAME}', AmbientWxy);
`;

export class AmbientAudio {
  private ctx: AudioContext | null = null;
  private node: AudioWorkletNode | null = null;
  private wanted = false;                   // the viewer asked for sound (survives a reconnect of the socket)
  private starting: Promise<void> | null = null;
  private warned = false;
  private heading = Number.NaN;              // the last heading sent to the worklet (NaN: none yet, so the first call always goes)
  private readonly seen: AmbientStats = {
    received: 0, gaps: 0, underruns: 0, overruns: 0, resets: 0, buffered: 0, playing: false, gainDb: 0,
  };

  /** `socket` returns the current broadcast socket (it changes when the client reconnects). `onUnavailable` is told why when the server cannot carry the sound. */
  constructor(private readonly socket: () => WebSocket | null, private readonly onUnavailable?: (why: string) => void) {}

  get enabled(): boolean { return this.wanted; }
  get stats(): Readonly<AmbientStats> { return this.seen; }

  /** Start the sound. Must run inside a user gesture. Resolves when audio is running and the server has been asked for the stream. */
  enable(): Promise<void> {
    this.wanted = true;
    if (this.starting) return this.starting;
    this.starting = this.start().catch(e => { this.wanted = false; this.teardown(); throw e; }).finally(() => { this.starting = null; });
    return this.starting;
  }

  /** Stop the sound and tell the server to stop sending it. */
  disable(): void {
    this.wanted = false;
    this.sendOptin(false);
    this.teardown();
  }

  /** The socket has (re)opened: ask again for the stream if the viewer wants it. */
  onSocketOpen(): void {
    if (this.wanted && this.node) this.sendOptin(true);
  }

  /** The direction the viewer faces, radians counter-clockwise from east (the world's x axis; north is pi/2). Call every frame. */
  setHeading(rad: number): void {
    if (!this.node || Math.abs(Math.atan2(Math.sin(rad - this.heading), Math.cos(rad - this.heading))) < 5e-4) return;     // unchanged (to a few hundredths of a degree, wrapping)
    this.heading = rad;
    this.node.port.postMessage({ heading: [Math.cos(rad), Math.sin(rad)] });
  }

  /** A binary websocket message that isAudioFrame() accepted. */
  ingest(buf: ArrayBuffer): void {
    const node = this.node;
    if (!node || !this.wanted) return;                       // sound was switched off while frames were in flight
    const v = new DataView(buf);
    const ok = buf.byteLength >= AUDIO_HEAD && v.getUint8(4) === AUDIO_CHANNELS && v.getUint32(12, true) === AUDIO_RATE
      && v.getUint32(16, true) === AUDIO_BLOCK && buf.byteLength === AUDIO_HEAD + AUDIO_CHANNELS * AUDIO_BLOCK * 2;
    if (!ok) {
      if (!this.warned) { this.warned = true; console.warn('[sim] audio frame this client cannot play (rate, block or channels differ from the build):', buf.byteLength, 'bytes'); }
      return;
    }
    const pcm = new Int16Array(buf.slice(AUDIO_HEAD));
    const gainDb = v.getFloat32(20, true);
    node.port.postMessage({ seq: v.getUint32(8, true), gainDb, pcm }, [pcm.buffer]);
  }

  /** The server answered the opt-in with {"type":"audio_unavailable","why":...}: stop, and tell whoever shows the button. */
  unavailable(why: string): void {
    this.wanted = false;
    this.teardown();
    this.onUnavailable?.(why);
  }

  dispose(): void { this.disable(); }

  private async start(): Promise<void> {
    const ctx = new AudioContext({ sampleRate: AUDIO_RATE, latencyHint: 'playback' });
    this.ctx = ctx;
    if (ctx.sampleRate !== AUDIO_RATE) throw new Error(`this browser runs audio at ${ctx.sampleRate} Hz and did not honour the ${AUDIO_RATE} Hz the stream needs`);
    const url = URL.createObjectURL(new Blob([WORKLET_SRC], { type: 'text/javascript' }));
    try { await ctx.audioWorklet.addModule(url); } finally { URL.revokeObjectURL(url); }
    const node = new AudioWorkletNode(ctx, WORKLET_NAME, {
      numberOfInputs: 0, numberOfOutputs: 1, outputChannelCount: [SPEAKERS],
      processorOptions: { block: AUDIO_BLOCK, prime: PRIME_BLOCKS, maxQueue: MAX_QUEUE_BLOCKS },
    });
    node.port.onmessage = (e: MessageEvent<AmbientStats>) => { Object.assign(this.seen, e.data); };
    const split = ctx.createChannelSplitter(SPEAKERS);
    node.connect(split);
    // Virtual loudspeakers, counter-clockwise from the front: front left, back left, back right, front right. Web Audio's listener faces -z with +x to the right,
    // so a speaker at angle th (left positive) sits at (-sin th, 0, -cos th). No distance attenuation: only the direction matters.
    const angles = [0.25, 0.75, -0.75, -0.25].map(v => v * Math.PI);
    angles.forEach((th, j) => {
      const pan = ctx.createPanner();
      pan.panningModel = 'HRTF';
      pan.distanceModel = 'linear';
      pan.refDistance = 1; pan.maxDistance = 2; pan.rolloffFactor = 0;
      pan.positionX.value = -Math.sin(th); pan.positionY.value = 0; pan.positionZ.value = -Math.cos(th);
      split.connect(pan, j, 0);
      pan.connect(ctx.destination);
    });
    this.node = node;
    await ctx.resume();
    this.sendOptin(true);
  }

  private teardown(): void {
    const ctx = this.ctx;
    this.node?.port.close();
    this.node?.disconnect();
    this.node = null;
    this.ctx = null;
    this.warned = false;
    this.heading = Number.NaN;
    if (ctx) void ctx.close();
  }

  private sendOptin(on: boolean): void {
    const ws = this.socket();
    if (ws && ws.readyState === WebSocket.OPEN) {
      try { ws.send(JSON.stringify({ type: 'audio', on })); } catch { /* socket not ready: onSocketOpen asks again */ }
    }
  }
}
